import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "@/components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Award,
  ChevronRight,
  Clock,
  Droplets,
  GraduationCap,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Sparkles,
  Sun,
  SunMedium,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/chemical-peels-mumbai";
const FILLERS_PATH = "/dermal-fillers-mumbai";
const BOTOX_PATH = "/botox-treatment-mumbai";
const PRP_PATH = "/prp-therapy-hair-loss-mumbai";
const NON_SURGICAL_PATH = "/non-surgical-treatments-in-mumbai";

const PEEL_FAQS = [
  {
    question: "How many chemical peel sessions do I need to see results?",
    answer:
      "For superficial peels, improvement in glow and skin clarity is visible after a single session but the best results accumulate over 4 to 6 sessions. For medium peels addressing pigmentation or acne scarring, 3 to 4 sessions produce meaningful improvement. Results continue to develop between sessions as the collagen remodeling and cell turnover effects of each peel build on the previous one.",
  },
  {
    question: "Are chemical peels safe for Indian skin?",
    answer:
      "Yes, when the peel is correctly selected and performed with appropriate pre- and post-peel protocols specific to Indian skin types. Indian skin is predominantly Fitzpatrick type III to V, which is more prone to post-inflammatory hyperpigmentation after skin insult. Safe peel treatment for Indian skin requires skin type assessment, pre-peel depigmenting preparation for medium and deeper peels, conservative concentration selection, and strict sun protection. Peels selected without accounting for skin type can worsen pigmentation rather than improve it.",
  },
  {
    question: "Will a chemical peel remove my acne scars completely?",
    answer:
      "Superficial and medium peels significantly improve the appearance of acne scars, particularly macular scars and shallow boxcar scars, by stimulating collagen and accelerating cell turnover. Complete removal of deep structural scars, particularly icepick and rolling scars, is not achievable with peels alone. Deep structural acne scars often benefit from combination treatment including peels alongside microneedling, subcision, or laser depending on the scar type. Dr. Jain assesses the specific scar types present and recommends the most effective combination for each patient.",
  },
  {
    question: "Is a chemical peel painful?",
    answer:
      "Superficial peels cause mild tingling or a slight warming sensation during application that resolves quickly. Medium peels cause more significant tingling and a warm, stinging sensation during the application that is manageable. Deep peels require appropriate analgesia and clinical monitoring. None of the commonly used superficial or medium peels are described as significantly painful by patients who have experienced them.",
  },
  {
    question: "How long does recovery take after a medium peel?",
    answer:
      "The skin reddens on the day of treatment and begins to peel on days 3 to 4. Active peeling continues for 5 to 7 days. The skin beneath the peeling layer is pink and sensitive. Most patients are comfortable returning to social and work settings after 7 days. Sun protection is strictly required from day 1 after the peel. The improved skin texture and tone are visible once peeling is complete and redness has settled, typically at 1 to 2 weeks.",
  },
  {
    question: "Can I combine chemical peels with other skin treatments?",
    answer:
      "Yes. Chemical peels are frequently combined with PRP therapy, which accelerates healing and enhances the collagen induction effect. Peels and laser treatments address different aspects of skin quality and are used in planned alternating sequences in comprehensive skin management programs. Microneedling and peels are also combined for acne scar treatment, with the specific sequence determined by the skin concern and the recovery timing of each treatment.",
  },
  {
    question: "What sun protection do I need after a chemical peel?",
    answer:
      "Broad-spectrum SPF 50 sunscreen applied every 2 hours during sun exposure is the minimum requirement. Physical sunblocks containing zinc oxide or titanium dioxide are preferred over chemical sunscreens during the immediate post-peel period when the skin barrier is recovering. Sun avoidance during peak UV hours, protective clothing, and hats provide additional protection. In Indian climate conditions, sun protection must be rigorous and consistent throughout the treatment course and maintained indefinitely after to preserve the peel benefit and prevent pigmentation recurrence.",
  },
  {
    question: "Can chemical peels help with melasma?",
    answer:
      "Yes, with careful management. Melasma responds to peels that reduce epidermal melanin content and accelerate cell turnover. However, melasma is a chronic condition influenced by hormonal factors and UV exposure that continues to recur without ongoing management. Peels improve melasma during the treatment course but do not cure the underlying tendency. Pre-peel preparation with depigmenting agents, conservative peel selection appropriate for the skin type, strict sun protection, and maintenance treatment after the initial course are all required for meaningful and sustained improvement. Aggressive peel treatment in melasma-prone skin can cause paradoxical worsening through post-inflammatory hyperpigmentation.",
  },
];

const skinBodyLocation = { "@type": "AnatomicalStructure", name: "Skin" };
const plasticSpecialty = "https://schema.org/PlasticSurgery";
const percutaneousType = "https://schema.org/PercutaneousProcedure";

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalProcedure",
      name: "Chemical peel treatment",
      description:
        "Minimally invasive controlled chemical resurfacing of facial or body skin to improve texture, pigmentation, and photoaging with depth matched to indication and skin type.",
      procedureType: percutaneousType,
      bodyLocation: skinBodyLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Superficial chemical peel",
      description: "Epidermal-only peel using glycolic, lactic, mandelic, or salicylic acid for maintenance, dullness, and mild acne with minimal downtime.",
      procedureType: percutaneousType,
      bodyLocation: skinBodyLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Medium-depth chemical peel",
      description: "TCA-based peel reaching upper dermis for moderate pigmentation, acne scarring, and fine lines with structured recovery.",
      procedureType: percutaneousType,
      bodyLocation: skinBodyLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Deep chemical peel",
      description: "Phenol or equivalent deep resurfacing for severe photoaging in selected lighter skin types under monitored clinical conditions.",
      procedureType: percutaneousType,
      bodyLocation: skinBodyLocation,
      medicalSpecialty: plasticSpecialty,
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: PEEL_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function ChemicalPeelJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Non-Surgical Treatments", href: NON_SURGICAL_PATH },
    { name: "Chemical Peels", href: "/chemical-peels-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Non-Surgical</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">Chemical Peels</h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Skin Resurfacing &amp; Renewal Treatments by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Chemical Peel" icon="🌟" size="hero"  imageSrc={HERO.chemicalPeel} imageAlt="Chemical peel treatment"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What is a Chemical Peel?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A chemical peel is a skin resurfacing procedure where a chemical solution is applied to the skin to remove damaged outer layers and stimulate the growth of new, healthier skin beneath. The depth of the peel, determined by the chemical used and its concentration, controls how many layers are removed and consequently how significant the improvement is and how much recovery time is needed.
            </p>
            <p>
              The principle behind chemical peels is controlled damage. By removing the outer damaged skin layers in a predictable, controlled manner, the body's healing response is triggered: collagen production increases, cell turnover accelerates, and the new skin that replaces the treated layers has better texture, more even pigmentation, and improved surface quality. The degree of this improvement is proportional to the depth of the peel. Superficial peels produce modest improvement with minimal recovery. Deep peels produce dramatic improvement with significant recovery.
            </p>
            <p>
              Dr. Vicky Jain provides chemical peel treatments as standalone skin treatments and as part of comprehensive skin management plans for acne, pigmentation, and aging. Peel selection is based on a detailed skin assessment including skin type, Fitzpatrick classification, current skin concerns, prior treatment history, and downtime tolerance. Patients with darker skin types, which includes most Indian patients, require specific peel protocols to achieve improvement without triggering post-inflammatory hyperpigmentation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Chemical Peel Treatment</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The most important decision in chemical peel treatment is matching the peel depth to the skin concern, the skin type, and the patient's realistic downtime tolerance. A patient with melasma on a Fitzpatrick type IV skin, common in the Indian population, requires a fundamentally different peel protocol than a patient with Fitzpatrick type II skin addressing fine lines. The same TCA concentration that produces excellent results in lighter skin can cause significant post-inflammatory hyperpigmentation in darker skin, worsening the pigmentation problem it was meant to treat. Pre-peel skin preparation with depigmenting agents and sun protection reduces this risk before any medium or deep peel is performed.
              </p>
              <p>
                Dr. Jain does not apply a standard peel to all patients. The concentration, application time, and neutralization are all adjusted based on how the skin is responding during the procedure. Skin responses, including frosting patterns for TCA peels, guide the depth of peel being achieved in real time. A session that begins as a planned medium peel is modified based on the observed skin response rather than applied to a fixed protocol regardless of what the skin is showing.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SkinConcernsSection() {
  const cards = [
    {
      title: "Acne and Oily Skin",
      icon: Droplets,
      copy: "Salicylic acid peels penetrate the sebaceous follicle, dissolve the sebum plug causing blackheads and whiteheads, and reduce the bacterial load contributing to active acne. Multiple sessions improve active acne while simultaneously addressing the surface skin quality around existing lesions.",
    },
    {
      title: "Acne Scars",
      icon: Activity,
      copy: "Superficial acne scars including macular scars, shallow boxcar scars, and post-inflammatory dark marks respond to medium-depth peels that stimulate collagen remodeling and accelerate the fading of pigmented marks. Deeper icepick and structural scars require deeper treatments or combination with microneedling.",
    },
    {
      title: "Pigmentation and Melasma",
      icon: SunMedium,
      copy: "Glycolic acid and TCA peels reduce melanin in the superficial skin layers and accelerate the shedding of pigmented cells. Melasma in Indian skin requires careful peel selection and mandatory pre- and post-peel depigmenting protocols to prevent rebound darkening. Multiple sessions with sun protection are required for sustained improvement.",
    },
    {
      title: "Dull or Uneven Skin Tone",
      icon: Sparkles,
      copy: "Superficial peels accelerate cell turnover, removing the accumulated dead skin layer that makes skin appear dull, uneven, and rough. Patients see brightening and improved clarity within days of a superficial peel session. This is the most immediate and accessible improvement chemical peels produce.",
    },
    {
      title: "Fine Lines and Early Aging",
      icon: Minus,
      copy: "Medium-depth peels stimulate collagen production in the dermis, improving the fine lines and surface texture changes of early photoaging. The improvement in skin quality from collagen induction continues for months after the peel session as new collagen matures.",
    },
    {
      title: "Sun Damage and Tanning",
      icon: Sun,
      copy: "UV-damaged skin with irregular pigmentation, rough texture, and solar lentigines responds to glycolic acid and TCA peels that remove the superficial damaged layers. Combined with consistent sun protection after treatment, the improvement in skin tone is maintained.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="skin-concerns-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Skin Concerns Treated with Chemical Peels</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PEEL_TYPES = [
  {
    title: "Superficial Peels",
    body: "Target the epidermis only. The most commonly used peels for routine skin maintenance and mild concerns. No significant downtime. Treated skin may appear slightly pink for a few hours. Common acids include glycolic acid (10 to 30 percent), lactic acid, mandelic acid, and salicylic acid (20 to 30 percent). Appropriate for all skin types including darker Indian skin tones. Multiple sessions spaced 2 to 4 weeks apart are needed for cumulative improvement. Best for oily skin, mild acne, dullness, and uneven tone.",
  },
  {
    title: "Medium-Depth Peels",
    body: "Penetrate to the upper dermis. Produce more significant skin renewal and collagen stimulation than superficial peels. Require a recovery period of 5 to 7 days during which skin peels and appears red before settling to reveal improved skin. Trichloroacetic acid (TCA) at 15 to 35 percent is the most commonly used medium-depth peel agent. Require pre-peel skin preparation with depigmenting agents and sun protection, particularly for Indian skin types, to reduce the risk of post-inflammatory hyperpigmentation. Best for moderate pigmentation, acne scarring, and fine lines.",
  },
  {
    title: "Deep Peels",
    body: "Penetrate into the mid-dermis. Produce dramatic improvement in severe wrinkles, deep sun damage, and significant scar texture. Phenol peels are the primary deep peel agent. Require 7 to 14 days of recovery with significant peeling, redness, and temporary skin sensitivity. Deep peels carry cardiac monitoring requirements due to systemic phenol absorption and must be performed in an appropriate clinical setting. Not appropriate for darker skin types due to permanent depigmentation risk. Best reserved for severe photoaging in lighter skin types under expert supervision.",
  },
  {
    title: "Combination Peel Protocols",
    body: "Multiple peel agents are combined in a single session to address different skin layers or concerns simultaneously. Yellow peels, Jessner's solution, and modified combination protocols allow customization of depth and mechanism for specific skin types and concerns. These protocols allow medium-depth results with better safety profiles for Indian skin compared to straight TCA at equivalent concentrations.",
  },
];

function TypesOfPeelsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="types-of-peels-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Types of Chemical Peels</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {PEEL_TYPES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function IndianSkinSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="indian-skin-section" aria-labelledby="indian-skin-heading">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Important Note</p>
        <h2 id="indian-skin-heading" className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
          Chemical Peels for Indian Skin: Special Considerations
        </h2>
        <Alert className="rounded-none border-2 border-gold/50 bg-white max-w-4xl mx-auto py-6 px-4 md:px-6">
          <AlertTriangle className="h-5 w-5 text-gold" aria-hidden />
          <AlertTitle className="font-serif text-lg text-premium-purple pl-7 mb-2">Important for Indian &amp; darker skin types</AlertTitle>
          <AlertDescription className="pl-7 text-deep-navy/80 space-y-6">
            <p className="leading-relaxed">
              Indian skin types fall predominantly in the Fitzpatrick classification range of III to V, characterized by higher melanin content that provides natural UV protection but also predisposes to post-inflammatory hyperpigmentation after any skin insult, including chemical peels. This means that peels appropriate for lighter skin types can worsen pigmentation in Indian patients if selection and aftercare protocols are not adapted to the skin type.
            </p>
            <p className="leading-relaxed">
              Safe and effective chemical peel treatment for Indian skin requires Fitzpatrick skin typing before any peel, mandatory pre-peel preparation with hydroquinone, kojic acid, retinoids, or azelaic acid for 4 to 6 weeks before medium or deep peels, conservative peel selection favoring superficial and low-concentration medium peels over aggressive deep peels, strict sun protection during the treatment course and indefinitely after, and management of any post-inflammatory hyperpigmentation that develops with appropriate depigmenting agents before proceeding with the next session. These are not optional add-ons. They are the clinical standard for peel treatment in this skin type.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    </section>
  );
}

function HowPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-performed-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">How Chemical Peel Treatment is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Each session takes 20 to 30 minutes in the clinic. The skin is cleaned thoroughly and all skincare products removed before the peel solution is applied. For medium and deep peels, a pre-peel degreasing step removes surface oils that would prevent uniform peel penetration.
            </p>
            <p>
              The chemical solution is applied evenly to the treatment area using a brush or gauze. For superficial peels, a neutralizing agent is applied after a specified time to stop the chemical reaction. For TCA peels, neutralization is not required as TCA self-neutralizes on contact with tissue proteins. The skin is observed throughout the application for the expected response: erythema and mild frosting for TCA peels, depending on concentration.
            </p>
            <p>
              After the peel is complete, a bland moisturizer and broad-spectrum sunscreen are applied. Detailed aftercare instructions are provided. Patients leave the clinic immediately after treatment. The recovery experience depends on peel depth: superficial peels allow immediate return to normal activity; medium peels require 5 to 7 days of recovery with peeling skin that should not be disturbed.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Superficial Peel",
      description:
        "Mild redness for a few hours. No visible peeling in most cases. Patients return to normal activity immediately. Glow and improved clarity are visible within 2 to 3 days. Repeat sessions every 2 to 4 weeks.",
      icon: Sparkles,
    },
    {
      period: "Medium Peel",
      description:
        "Redness and tightness on the day of treatment. Peeling begins on days 3 to 4 and continues for 5 to 7 days. The peeling skin should not be pulled or picked. Normal activity with sun avoidance resumes after 5 to 7 days.",
      icon: Monitor,
    },
    {
      period: "Deep Peel",
      description:
        "Significant recovery of 10 to 14 days. Skin is red, swollen, and actively peeling during this period. Social and professional activity is restricted. Medical supervision during recovery is required.",
      icon: AlertTriangle,
    },
    {
      period: "All Peels: Ongoing",
      description:
        "Sun protection with SPF 50 is mandatory during the entire treatment course and indefinitely after, particularly in the Indian climate. UV exposure during healing causes post-inflammatory hyperpigmentation that reverses the peel benefit.",
      icon: Sun,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline by Peel Depth</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-white/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-white/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-white mb-2">{milestone.period}</h4>
                  <p className="text-sm text-white/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-white/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-white/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-white mb-2">{milestone.period}</h4>
                  <p className="text-sm text-white/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SessionsNeededSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="sessions-needed-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Treatment Plan</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">How Many Chemical Peel Sessions Are Needed?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The number of sessions depends on the concern being treated, the peel depth selected, and the degree of improvement targeted. For acne and general skin maintenance with superficial peels, 4 to 6 sessions spaced 2 to 4 weeks apart produce the most meaningful cumulative improvement. Individual sessions show modest improvement. The benefit compounds with each successive treatment as collagen induction and cell turnover improvement accumulate.
            </p>
            <p>
              For pigmentation and acne scarring with medium-depth peels, 3 to 4 sessions spaced 4 to 6 weeks apart are typical depending on the severity of the initial concern and the skin response to treatment. A single medium peel produces noticeable improvement but not the full correction achievable with a planned course. After the treatment course, maintenance sessions at 3 to 6 month intervals preserve the improvement and prevent recurrence of acne and pigmentation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function GoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="good-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Who Is a Good Candidate for Chemical Peels?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates are adults with active acne, acne scarring, pigmentation, melasma, dull skin, uneven tone, or early photoaging who have stable skin without active infection or open wounds at the treatment site. Patients who are consistent about sun protection are the most appropriate candidates because the improvement from peels is significantly undermined by UV exposure during and after the treatment course.
            </p>
            <p>
              Chemical peels are not appropriate during pregnancy, as certain peel agents including salicylic acid and retinoids are contraindicated. Patients with active cold sores should not have peels over affected areas as trauma can trigger outbreaks. Patients who have used isotretinoin within the past 6 to 12 months require a waiting period before medium or deep peels as the medication affects skin healing capacity. Patients with very dark skin tones or a strong personal history of post-inflammatory hyperpigmentation should start with the most conservative superficial peel options and progress cautiously under close monitoring.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Chemical" />;
}

function FAQSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {PEEL_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`peel-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Chemical Peel Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your skin type, Fitzpatrick classification, specific concerns, and prior treatment history to select the peel type and concentration appropriate for your skin. For Indian skin types, the pre-peel preparation protocol is designed before the first peel session to ensure safe and effective treatment. You leave with a clear treatment plan and practical guidance on what to expect at each stage.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Dermal Fillers",
      description: "Volume restoration to complement surface skin improvement from peels for complete facial rejuvenation.",
      href: FILLERS_PATH,
    },
    {
      name: "Botox",
      description: "Muscle-driven wrinkle treatment alongside surface skin quality improvement from chemical peels.",
      href: BOTOX_PATH,
    },
    {
      name: "PRP Therapy",
      description: "Growth factor treatment that enhances healing and collagen induction when combined with chemical peels.",
      href: PRP_PATH,
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ChemicalPeelPage() {
  return (
    <>
      <SEO
        title="Chemical Peels | Dr. Vicky Jain"
        description="Acne marks, pigmentation, dull skin, and early aging treated with chemical peels. Dr. Vicky Jain provides customized peel treatments. Book your consultation."
        canonical="/chemical-peels-mumbai"
      />
      <ChemicalPeelJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <SkinConcernsSection />
      <TypesOfPeelsSection />
      <IndianSkinSection />
      <HowPerformedSection />
      <RecoveryTimelineSection />
      <SessionsNeededSection />
      <GoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
